import { Close } from "@mui/icons-material";
import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Typography } from "@mui/material";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import TableComp from "src/components/shared/table/TableComp";
import appConfig from "src/config/env";
import { COMMOM_STATUS, orderStatusFormatted } from "src/constant";
import useAppGlobal from "src/context/useAppGlobal";
import useProcessLogDetail from "src/hooks/process-log/useProcessLogDetail";
import useSettingBatchCode from "src/hooks/setting/batch-code/useSettingBatchCode";
import useDebounce from "src/hooks/useDebounce";
import useWarehouseTransferExport from "src/hooks/warehouse-transfer/useWarehouseTranferExport";
import useWarehouseTransferReview from "src/hooks/warehouse-transfer/useWarehouseTransferReview";
import useWarehouseTransferStatusIn from "src/hooks/warehouse-transfer/useWarehouseTransferStatusIn";
import useWTScanCreate from "src/hooks/warehouse-transfer/useWTScanCreate";
import DeliveredReceipt from "src/pages/orders/all-order/export-form/DeliveredReceipt";
import useImportProdBody from "./utils/useImportProdBody";

export default function ImportProdDialog({ item }) {
  const { closeDialog } = useAppGlobal();
  const [prodPick, setProdPick] = useState();
  const [prodList, setProdList] = useState([]);
  const [indiProd, setIndiProd] = useState();
  const [scannerResetKey, setScannerResetKey] = useState(0);
  const [isSent, setIsSent] = useState(null);
  const [logId, setLogId] = useState();
  const debounceVal = useDebounce(indiProd?.importItemCodes, 200);
  const debounceReturnQty = useDebounce(indiProd?.returnQty, 600);

  const { trigger: sendAll } = useWarehouseTransferStatusIn(item?._id);
  const { data: review, isLoading: reviewLoading } = useWarehouseTransferReview(isSent, {
    transferFileType: orderStatusFormatted.Imported,
  });
  const { mutate } = useProcessLogDetail(logId);
  const { trigger: printWT } = useWarehouseTransferExport();
  const { data: batchCode } = useSettingBatchCode();
  const { trigger: scanCode } = useWTScanCreate(item?._id);
  const { header, bodyList, sortData, loading, detailMutate, detail, handleDisplayError, isFull } = useImportProdBody({
    item,
    setProdList,
    prodList,
    setIndiProd,
    indiProd,
    setProdPick,
    scannerResetKey,
  });

  const handlePrint = async () => {
    const res = await printWT({
      body: {
        warehouseTransferIds: [item?._id],
        transferFileType: orderStatusFormatted.Imported,
      },
    });
    if (res?.success) {
      setLogId(res?.data?._id);
      handleGetLog(res?.data?._id);
      return enqueueSnackbar("Tiến hành xuất phiếu....", { variant: "success", className: "notistack-confirm" });
    } else {
      return enqueueSnackbar(res?.error?.message ?? res?.error?.message, {
        variant: "error",
      });
    }
  };

  const handleGetLog = (logIdParam) => {
    const logIdToUse = logIdParam || logId;
    if (!logIdToUse) return;

    const timeOut = setInterval(async () => {
      try {
        const res = await mutate();
        if (res?.success) {
          if (res.data.status === COMMOM_STATUS.done) {
            closeDialog();
            if (Array.isArray(res.data?.urls)) {
              res.data.urls.forEach((v) => {
                window.open(`${appConfig.API_FILE}/${v}`, "_blank");
              });
            }
            clearInterval(timeOut);
          }

          if (res.data.status === COMMOM_STATUS.error) {
            enqueueSnackbar("Không xuất được đơn", { variant: "error" });
            clearInterval(timeOut);
          }
        }
      } catch (err) {
        clearInterval(timeOut);
      }
    }, 1000);
  };

  useEffect(() => {}, [prodPick]);

  const handleChangeValue = (name, value) => {
    setIndiProd((prev) => ({ ...prev, prodPick, [name]: value }));
  };

  // CONFIRM RECEIVE ALL PRODUCT
  const handleImport = async () => {
    const res = await sendAll({
      body: {
        warehouse: item?.toWarehouse?._id,
        statusIn: orderStatusFormatted.Imported,
      },
    });
    if (res?.success) {
      enqueueSnackbar("Xác nhận nhập hàng thành công!", { variant: "success" });
      return setIsSent(res?.data?._id);
    } else {
      return enqueueSnackbar(res?.error?.message, { variant: "error" });
    }
  };

  // SCAN FOR EACH PRODUCT
  const handleScan = async (codes) => {
    const res = await scanCode({
      pathIncluded: {
        transferDetailId: prodPick?._id,
      },
      body: {
        itemCodes: codes,
        returnQty: Number(indiProd?.returnQty ?? 0),
        note: indiProd?.note,
      },
    });
    if (res?.success) {
      if (res?.data?.transferDetail?.importedQty + res?.data?.transferDetail?.returnQty === res?.data?.transferDetail?.qty) {
        setProdPick();
        setIndiProd();
        return enqueueSnackbar("Đủ số lượng", { variant: "success" });
      }
      if (res?.data?.itemCodes?.totalError === 0) {
        setIndiProd((prev) => ({ ...prev, importItemCodes: undefined }));
        detailMutate();
      }
      if (res?.data?.itemCodes?.totalError > 0) {
        handleDisplayError(res?.data?.itemCodes?.errorItemCodes);
      }
    } else {
      return enqueueSnackbar(res?.error?.message);
    }
  };

  // SPLIT & FILTER CODE
  const handleFilterCode = () => {
    if (!Array.isArray(batchCode?.data?.prefixItemCodes) || !Array.isArray(batchCode?.data?.stripItemCodes)) return;
    const refinedArray = debounceVal?.map((v) => v?.replaceAll("Shift", ""));

    const validCodes = [];
    const invalidCodes = [];

    refinedArray.forEach((line) => {
      let cleaned = line;

      batchCode.data.stripItemCodes.forEach((strip) => {
        if (cleaned.startsWith(strip + "/")) {
          cleaned = cleaned.replace(strip + "/", "");
        } else if (cleaned.startsWith(strip)) {
          cleaned = cleaned.replace(strip, "");
        }
      });

      const hasValidPrefix = batchCode.data.prefixItemCodes.some((prefix) => cleaned.startsWith(prefix));

      if (hasValidPrefix) {
        validCodes.push(cleaned);
      } else {
        invalidCodes.push(cleaned);
      }
    });

    handleScan(validCodes);
  };

  // TRIGGER RETURN QTY
  useEffect(() => {
    if (debounceReturnQty) {
      handleScan();
      enqueueSnackbar(`Số lượng cần trả ${indiProd?.returnQty}`, {
        variant: "success",
      });
    }
  }, [debounceReturnQty]);

  // TRIGGER SCAN EACH PRODUCT CODE
  useEffect(() => {
    if (debounceVal) {
      handleFilterCode();
    }
  }, [debounceVal]);

  const totalTransferDetail = Array.isArray(detail?.data?.transferDetails) ? detail?.data?.transferDetails?.find((v) => v?._id === prodPick?._id) : 0;

  const handleClose = () => {
    closeDialog();
  };

  const hasPartialScannedQty = detail?.data?.transferDetails?.some((transferDetail) => {
    const importedQty = Number(transferDetail?.importedQty ?? 0);
    const returnQty = Number(transferDetail?.returnQty ?? 0);
    const totalQty = Number(transferDetail?.qty ?? 0);
    const completedQty = importedQty + returnQty;
    return completedQty > 0 && completedQty < totalQty;
  });

  const handleConfirmPerProd = (val) => {
    if (!Array.isArray(detail?.data?.transferDetails)) return;
    for (const prod of detail?.data?.transferDetails) {
      const importedQty = Number(prod?.importedQty ?? 0);
      const returnQty = Number(prod?.returnQty ?? 0);
      const totalQty = Number(prod?.qty ?? 0);
      const completedQty = importedQty + returnQty;
      if (completedQty > 0 && completedQty < totalQty) {
        enqueueSnackbar("Có sản phẩm đang scan dở, vui lòng scan đủ mã cho sản phẩm đó.", { variant: "error" });
        return;
      }
    }

    enqueueSnackbar("Nhập sản phẩm thành công", { variant: "success" });
    setScannerResetKey((prev) => prev + 1);
    if (totalTransferDetail?.importedQty === 0) {
      const index = prodList?.findIndex((v) => v?.batch?._id === prodPick?.batch?._id);
      if (index > -1) {
        const prod = [...prodList];
        prod[index] = { ...prod[index], progress: "Nhập đủ" };
        setProdList(prod);
        setProdPick();
      }
    }
  };

  const active = totalTransferDetail?.importedQty > 0 && Number(prodPick?.qty) > totalTransferDetail?.importedQty;

  return (
    <>
      <DialogTitle
        fontWeight="bold"
        display="flex"
        justifyContent="space-between"
        sx={{ borderBottom: "1px solid var(--grey)", mb: "8px" }}
      >
        {!isSent && "Xác nhận nhập hàng"}
        <IconButton
          onClick={handleClose}
          sx={{ width: "24px", height: "24px", ml: "auto" }}
        >
          <Close sx={{ fontSize: "22px" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflowX: "hidden" }}>
        {!isSent ? (
          <>
            <Box
              sx={{
                background: "var(--grey)",
                p: "12px",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                gap: "6px",
                mb: "12px",
              }}
            >
              <Typography>
                <strong>Kho xuất/NCC: </strong> {(item?.fromWarehouse?.name || item?.provider?.name) ?? "-"}
              </Typography>
              <Typography>
                <strong>Sản phẩm: </strong> {detail?.data?.transferDetails?.length ?? "-"}
              </Typography>
              <Typography>
                <strong>Số lượng: </strong> {item?.totalExported ?? "-"}
              </Typography>
              <Typography>
                <strong>Hình thức thanh toán: </strong> {item?.paymentMethod?.name ?? "-"}
              </Typography>
              <Typography>
                <strong>Ngày tạo yêu cầu Nhập hàng: </strong> {dayjs(item?.exportedAt).format("DD/MM/YYYY") ?? "-"}
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: "bold", fontSize: "16px", mb: "12px" }}>Danh sách sản phẩm</Typography>
            <TableComp
              dynamicWidth={"400px"}
              dynamicHeight={"350px"}
              activeRow={prodPick}
              headers={header}
              bodyList={bodyList}
              sortData={sortData}
              isLoading={loading}
            />
            <Box sx={{ display: "flex", flexDirection: "column", gap: "16px", mt: "12px" }}>
              <TextField
                // value={indiProd?.returnQty || ""}
                label="Nhập số hoá đơn (Nếu có)"
                fullWidth
                // onChange={(e) => handleChangeValue("returnQty", e.target.value)}
              />
              <TextField
                value={indiProd?.note || ""}
                multiline
                rows={6}
                label="Ghi chú"
                fullWidth
                onChange={(e) => handleChangeValue("note", e.target.value)}
              />
            </Box>
          </>
        ) : (
          <Box>
            <DeliveredReceipt
              isLoading={reviewLoading}
              html={review}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: "0 24px 24px" }}>
        {!isSent ? (
          <>
            {prodPick && (
              <Button
                variant="contained"
                disabled={active}
                fullWidth
                onClick={() => {
                  handleConfirmPerProd(prodPick);
                }}
              >
                Xác nhận nhập sản phẩm
              </Button>
            )}
            {!prodPick && (
              <Button
                disabled={hasPartialScannedQty || !isFull}
                onClick={handleImport}
                variant="contained"
                fullWidth
              >
                Xác nhận nhập hàng
              </Button>
            )}
          </>
        ) : (
          <Button
            fullWidth
            variant="contained"
            onClick={handlePrint}
          >
            In phiếu
          </Button>
        )}
      </DialogActions>
    </>
  );
}
